"use client"

import { useMemo, useCallback } from "react";
import type { RefObject } from "react";
import Link from "next/link";
import { ColumnDef } from "@tanstack/react-table";
import { IconButton } from "@/components/icon-button";
import { XIcon } from "@/components/fun-ui/icons";
import { ResultViewMemo } from "../ResultView.memo";
import { PaperInformationMemo } from "../PaperInformation.memo";
import { SelectionHeader, SelectionCell } from "../SelectionCells";
import { Paper, Col } from "@/stores/slices/paperSlice";
import SortSelect from "../SortSelect";

const INFORMATION_ITEMS = [
  { name: "机构", key: "organization" },
  { name: "作者", key: "author" },
  { name: "年份", key: "createAt" },
  { name: "期刊", key: "journal" },
] as const;

type UseTableColumnsParams = {
  cols: Col[];
  isSimplifyGrid: boolean;
  subjectId: string;
  getIsCurrentPageRowsSelected: (papers: Paper[]) => boolean | "indeterminate";
  toggleCurrentPageRowsSelected: (papers: Paper[], value: boolean) => void;
  getIsSelected: (paperId: string) => boolean;
  toggleSelected: (paperId: string, value: boolean) => void;
  setOrder: (order: any) => void;
  handleSetPaperCols: (colKeys: string[], cols: Col[], mode?: any) => Promise<void>;
  handleRetry: (paper: Paper, colKey: string) => void;
  handleRefresh: () => void;
  orderRef: RefObject<any>;
  isPolling?: boolean;
};

export function useTableColumns({
  cols,
  isSimplifyGrid,
  subjectId,
  getIsCurrentPageRowsSelected,
  toggleCurrentPageRowsSelected,
  getIsSelected,
  toggleSelected,
  setOrder,
  handleSetPaperCols,
  handleRetry,
  orderRef,
  isPolling = false,
  handleRefresh,
}: UseTableColumnsParams): ColumnDef<Paper>[] {
  // 稳定化 header 函数，避免每次渲染都重新创建
  const selectHeader = useCallback(({ table }: { table: any }) => {
    const rows = table.getRowModel().rows;
    const papers = rows.map((row: any) => row.original);
    const checked = getIsCurrentPageRowsSelected(papers);
    return (
      <SelectionHeader
        papers={papers}
        checked={checked}
        onToggle={(value) => toggleCurrentPageRowsSelected(papers, value)}
      />
    );
  }, [getIsCurrentPageRowsSelected, toggleCurrentPageRowsSelected]);

  // 稳定化 cell 函数，避免每次渲染都重新创建
  const selectCell = useCallback(({ row }: { row: any }) => {
    const paperId = row.original.paperId;
    return (
      <SelectionCell
        paperId={paperId}
        checked={getIsSelected(paperId)}
        onToggle={(value) => toggleSelected(paperId, value)}
      />
    );
  }, [getIsSelected, toggleSelected]);

  // 稳定化 information header 函数
  const informationHeader = useCallback(() => {
    return (
      <div className="flex flex-row items-center justify-between">
        <div>文献</div>
        <SortSelect value={orderRef.current} onSortChange={setOrder} />
      </div>
    );
  }, [setOrder, orderRef]);

  return useMemo(() => {
    const staticCols: ColumnDef<Paper>[] = [
      {
        id: "select",
        header: selectHeader,
        cell: selectCell,
        enableSorting: false,
        enableHiding: false,
      },
      {
        accessorKey: "information",
        header: informationHeader,
        cell: ({ row }) => {
          const paper = row.original as Paper;
          return (
            <PaperInformationMemo
              className=""
              paper={paper}
              subjectId={subjectId}
            />
          );
        },
        enableHiding: false,
        enablePinning: true,
      },
    ];

    const dynamicCols: ColumnDef<Paper>[] = cols
      .filter(({ selected }) => selected)
      .map((col) => {
        const { name, key } = col;
        return ({
          accessorKey: key,
          header: () => (
            <div className="min-w-[400px] flex items-center justify-between">
              <span>{name}</span>
              <IconButton
                label="关闭当前表格列"
                className="text-annotation-foreground"
                onClick={() => {
                  handleSetPaperCols([key], [col], 'del');
                }}
                Icon={XIcon}
              />
            </div>
          ),
          cell: ({ row }) => {
            const paper = row.original as Paper;
            const colData = paper.understands?.[key];
            return (
              <ResultViewMemo
                key={row.id}
                keyName={key}
                paperName={paper.paperName}
                taskId={colData?.taskId}
                result={(paper?.extractStatus && paper?.extractStatus < 0) ? paper?.error : colData?.result}
                images={colData?.images}
                tables={colData?.tables}
                status={colData?.status}
                extractStatus={paper?.extractStatus}
                onRetry={() => handleRetry(paper, key)}
                onRefresh={handleRefresh}
                isPolling={isPolling}
                attachmentId={paper.attachmentId}
              />
            );
          },
        });
      });

    const informationCols: ColumnDef<Paper>[] = INFORMATION_ITEMS.map(({ name, key }) => ({
      id: `information.result[${key}]`,
      accessorKey: `information.result[${key}]`,
      header: () => <div className="min-w-[260px]">{name}</div>,
      cell: ({ row }) => {
        const paper = row.original as Paper;
        const { result, status } = paper.information;
        return (
          <ResultViewMemo
            key={row.id}
            keyName={key}
            taskId={paper.paperId}
            paperName={paper.paperName}
            result={(paper?.extractStatus && paper?.extractStatus < 0) ? paper?.error : (result[(key as keyof typeof result)] as string)}
            status={status}
            extractStatus={paper?.extractStatus}
            onRetry={() => handleRetry(paper, key)}
            onRefresh={handleRefresh}
            isPolling={isPolling}
            className="min-w-[260px]"
            attachmentId={paper.attachmentId}
          />
        )
      },
      enableHiding: false,
      enablePinning: true,
    }));

    const informationCol: ColumnDef<Paper>[] = [{
      name: "基础信息",
      key: "information",
    } as any].map(({ name, key }) => ({
      id: `information.result[${key}]`,
      accessorKey: `information.result[${key}]`,
      header: () => <div className="min-w-[260px]">{name}</div>,
      cell: ({ row }) => {
        const paper = row.original as Paper;
        const { result, status } = paper.information;
        let label = INFORMATION_ITEMS.map((item) => {
          return `**${item.name}**：${result[(item.key as keyof typeof result)]}`;
        }).join('\n\n');
        if (status !== 2) {
          label = '';
        }
        return (
          <ResultViewMemo
            key={row.id}
            keyName={key}
            taskId={paper.paperId}
            paperName={paper.paperName}
            result={(paper?.extractStatus && paper?.extractStatus < 0) ? paper?.error : label}
            status={status}
            extractStatus={paper?.extractStatus}
            onRetry={() => handleRetry(paper, key)}
            onRefresh={handleRefresh}
            isPolling={isPolling}
            className="min-w-[260px]"
            attachmentId={paper.attachmentId}
          />
        )
      },
      enableHiding: false,
      enablePinning: true,
    }));

    const restCols: ColumnDef<Paper>[] = [
      {
        accessorKey: "currPage",
        header: () => <div className="min-w-[130px]">阅读进度</div>,
        cell: ({ row }) => {
          const paper = row.original as Paper;
          return (
            <div className="min-w-[130px]">
              {paper.totalPage ? Math.round(paper.currPage / paper.totalPage * 100) : 0}%
            </div>
          );
        },
        enableHiding: false,
        enablePinning: true,
      },
      {
        accessorKey: "noteCount",
        header: () => <div className="min-w-[130px]">笔记摘录</div>,
        cell: ({ row }) => {
          const paper = row.original as Paper;
          return (
            <div className="min-w-[130px]">
              <Link className="text-primary hover:underline" href={`/papers/${paper.paperId}?to=list`}>{paper.noteCount}</Link>
            </div>
          );
        },
        enableHiding: false,
        enablePinning: true,
      },
      {
        accessorKey: "createTime",
        header: () => <div className="min-w-[180px]">上传时间</div>,
        cell: ({ getValue }) => {
          return (
            <div className="min-w-[180px]">
              {getValue<string>()}
            </div>
          );
        },
        enableHiding: false,
        enablePinning: true,
      },
    ];

    return isSimplifyGrid
      ? staticCols.concat(informationCols).concat(restCols)
      : staticCols.concat(informationCol).concat(dynamicCols);
  }, [
    cols,
    isSimplifyGrid,
    subjectId,
    handleSetPaperCols,
    handleRetry,
    handleRefresh,
    isPolling,
    selectHeader,
    selectCell,
    informationHeader,
    // 注意：setOrder 已经在 informationHeader 的依赖项中
    // getIsCurrentPageRowsSelected, toggleCurrentPageRowsSelected, getIsSelected, toggleSelected 
    // 这些函数已经在 selectHeader 和 selectCell 的 useCallback 中使用了
  ]);
}


